import React, { useEffect, useState } from 'react';
import {
  CheckCircle2, AlertTriangle, Loader2, ListChecks,
  Calendar, CalendarDays, Users, Wrench, Clock, Info, Pencil,
} from 'lucide-react';
import { previewCapacidad } from '../api';
import { contenidoParaUsuario } from './editarSecciones';
import { resumenDias, resumenFranjas } from './diasResumen';

/**
 * Paso 7 — Verificación.
 *
 * Rediseño (Fase 3): a todo el ancho y en tarjetas (campaña, fechas y horario, días, técnicos,
 * personas, subtareas), al estilo de la revisión de Incorporaciones, con la capacidad que ya
 * calculaba (la misma llamada a preview-capacidad, una vez al entrar). Cada tarjeta lleva a su paso
 * con `irAPaso` (todos ya vistos). Los textos de usuario se pintan como texto, nunca HTML.
 */
const MAX_NOMBRES = 12;

export default function Paso7Verificacion({ payload, errores, irAPaso = null }) {
  const [capacidad, setCapacidad] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    previewCapacidad({
      fecha_inicio: payload.fecha_inicio,
      fecha_limite: payload.fecha_limite,
      horario_inicio: payload.horario_inicio,
      horario_fin: payload.horario_fin,
      duracion_cita: payload.duracion_cita,
      excluir_fines_de_semana: payload.excluir_fines_de_semana,
      excluir_festivos: payload.excluir_festivos,
      excluidos_manuales: payload.excluidos_manuales,
      tecnicos_ids: payload.tecnicos_ids,
      ambito: payload.ambito,
    })
      .then((res) => { if (!cancelado) setCapacidad(res); })
      .catch((e) => {
        if (!cancelado) {
          setError(
            e?.response?.data?.errores?.[0]?.mensaje ||
            e?.response?.data?.error ||
            'No se pudo calcular la capacidad.'
          );
        }
      })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line
  }, []);

  const usuariosCount = payload.usuarios_ids.length;
  const tecnicosCount = payload.tecnicos_ids.length;
  const esColaborador = payload.ambito === 'colaborador';
  const publico = contenidoParaUsuario(payload);
  const dias = resumenDias(payload);
  const franjas = resumenFranjas(payload.franjas);
  const nombres = (ids, cache) => ids.map((id) => cache?.[id]?.nombre_completo || `#${id}`);

  const capacidadInsuficiente =
    capacidad &&
    capacidad.slots_totales < usuariosCount &&
    usuariosCount > 0;

  return (
    <div className="space-y-4">
      {errores.length > 0 && (
        <div className="rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-v25-expired-fg">
            <AlertTriangle className="h-4 w-4" />
            La campaña no puede activarse aún
          </div>
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-v25-expired-fg">
            {errores.map((e, i) => (
              <li key={i}>
                <strong>{e.campo}</strong>: {e.mensaje}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Capacidad: lo que más importa antes de activar, arriba y a todo el ancho */}
      <section
        className={
          'rounded-v25-md border p-4 ' +
          (capacidadInsuficiente ? 'border-v25-waiting-bd bg-v25-waiting-bg' : 'border-v25-line bg-white')
        }
        aria-label="Capacidad estimada"
      >
        <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
          <Clock className="h-3.5 w-3.5" /> Capacidad estimada
        </h4>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-v25-ink-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Calculando…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : capacidad ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <Cifra v={capacidad.dias_totales} t={capacidad.dias_totales === 1 ? 'día con citas' : 'días con citas'} />
              <Cifra v={capacidad.slots_totales} t={capacidad.slots_totales === 1 ? 'hueco disponible' : 'huecos disponibles'} />
              {usuariosCount > 0 && <Cifra v={usuariosCount} t={usuariosCount === 1 ? 'persona a colocar' : 'personas a colocar'} />}
            </div>

            {capacidad.slots_por_tecnico?.length > 0 && (
              <ul className="grid gap-x-8 gap-y-0.5 text-xs text-v25-ink-500 sm:grid-cols-2 xl:grid-cols-3">
                {capacidad.slots_por_tecnico.map((t) => (
                  <li key={t.tecnico_id} className="flex justify-between gap-3">
                    <span className="truncate">{t.nombre || `Técnico #${t.tecnico_id}`}</span>
                    <span className="tabular-nums">{t.slots} huecos</span>
                  </li>
                ))}
              </ul>
            )}

            {capacidad.avisos?.length > 0 && (
              <div className="space-y-0.5 border-t border-v25-line pt-2 text-xs text-v25-waiting-fg">
                {capacidad.avisos.map((a, i) => (
                  <div key={i} className="flex items-start gap-1">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            )}

            {capacidadInsuficiente && (
              <div className="flex items-start gap-2 text-xs text-v25-waiting-fg">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  La capacidad ({capacidad.slots_totales}) es menor que el número de
                  usuarios ({usuariosCount}). Considera ampliar el rango, añadir
                  técnicos o reducir la duración de las citas.
                </span>
              </div>
            )}
          </div>
        ) : null}
      </section>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Tarjeta icono={Info} titulo="Campaña" paso={1} irAPaso={irAPaso}>
          <p className="font-medium text-v25-ink-900 break-words">{payload.titulo || '—'}</p>
          <p className="text-v25-ink-700">{esColaborador ? 'Colaboradores' : 'Internos'}</p>
          <p className="mt-2 text-xs text-v25-ink-500">
            La persona verá: <span className="text-v25-ink-700 break-words">«{publico.titulo || '—'}»</span>
            {publico.tituloEsInterno && ' (el título interno)'}
          </p>
          <Lista items={[
            `Asignación ${payload.asignacion_modo === 'libre' ? 'libre (elige la persona)' : 'automática'}`,
            `Hasta ${payload.max_cancelaciones_default} cancelaciones por persona`,
            payload.horas_antelacion_cancelar ? `Cancelar hasta ${payload.horas_antelacion_cancelar} h antes` : 'Cancelar sin antelación mínima',
            textoRecordatorios(payload),
          ]} />
        </Tarjeta>

        <Tarjeta icono={Calendar} titulo="Fechas y horario" paso={3} irAPaso={irAPaso}>
          <p className="font-medium text-v25-ink-900">
            {fechaCorta(payload.fecha_inicio)} – {fechaCorta(payload.fecha_limite)}
          </p>
          <p className="text-v25-ink-700">Citas de {payload.duracion_cita ?? '—'} min</p>
          {franjas.length === 0 ? (
            <p className="mt-2 text-xs text-v25-ink-500">Sin franjas.</p>
          ) : (
            <dl className="mt-2 space-y-0.5 text-xs">
              {franjas.map((f) => (
                <div key={f.dia} className="flex gap-2">
                  <dt className="w-9 shrink-0 text-v25-ink-500">{f.corto}</dt>
                  <dd className="tabular-nums text-v25-ink-700">{f.tramos.join(', ')}</dd>
                </div>
              ))}
            </dl>
          )}
        </Tarjeta>

        <Tarjeta icono={CalendarDays} titulo="Días" paso={4} irAPaso={irAPaso}>
          <p className="font-medium text-v25-ink-900">
            {capacidad && Number.isInteger(capacidad.dias_totales)
              ? `${capacidad.dias_totales} días disponibles`
              : `${dias.disponiblesAntesDeFestivos} días disponibles antes de festivos`}
          </p>
          <Lista items={[
            `${dias.totales} días en el rango`,
            payload.permite_fin_semana ? 'Con sábados y domingos' : `${dias.findes} de fin de semana fuera`,
            dias.excluidos.length ? `${dias.excluidos.length} excluido(s) a mano` : 'Ningún día excluido a mano',
          ]} />
        </Tarjeta>

        <Tarjeta icono={Wrench} titulo={`Técnicos (${tecnicosCount})`} paso={5} irAPaso={irAPaso}>
          <Chips nombres={nombres(payload.tecnicos_ids, payload._tecnicos_cache)} vacio="Ningún técnico." />
        </Tarjeta>

        <Tarjeta icono={Users} titulo={`Personas (${usuariosCount})`} paso={6} irAPaso={irAPaso}>
          <Chips nombres={nombres(payload.usuarios_ids, payload._usuarios_cache)} vacio="Ninguna persona." />
        </Tarjeta>

        <Tarjeta icono={ListChecks} titulo={`Subtareas (${payload.subtareas.length})`} paso={2} irAPaso={irAPaso}>
          {payload.subtareas.length === 0 ? (
            <p className="text-v25-ink-500">Sin checklist: la cita se da por hecha al completarla.</p>
          ) : (
            <ol className="list-decimal space-y-0.5 pl-5 text-v25-ink-700">
              {payload.subtareas.map((s, i) => <li key={i} className="break-words">{s.nombre || '—'}</li>)}
            </ol>
          )}
        </Tarjeta>
      </div>

      {!loading && !error && capacidad && capacidad.slots_totales > 0 && (
        <div className="flex items-center gap-2 rounded-v25-md border border-v25-active-bd bg-v25-active-bg px-3 py-2 text-sm text-v25-active-fg">
          <CheckCircle2 className="h-4 w-4" />
          <span>
            Todo listo. Pulsa <strong>Crear y activar</strong> para que los usuarios
            puedan empezar a reservar, o <strong>Guardar borrador</strong> para volver
            a editar más tarde.
          </span>
        </div>
      )}
    </div>
  );
}

function Tarjeta({ icono: Icono, titulo, paso, irAPaso, children }) {
  return (
    <section className="rounded-v25-md border border-v25-line bg-white p-4 text-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">
          <Icono className="h-3.5 w-3.5" /> {titulo}
        </h4>
        {irAPaso && (
          <button
            type="button"
            onClick={() => irAPaso(paso)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-v25-brand-600 hover:bg-v25-brand-600/5"
          >
            <Pencil className="h-3 w-3" /> Cambiar
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Cifra({ v, t }) {
  return (
    <div>
      <span className="text-2xl font-semibold tabular-nums text-v25-ink-900">{v}</span>
      <span className="ml-1.5 text-sm text-v25-ink-500">{t}</span>
    </div>
  );
}

function Lista({ items }) {
  return (
    <ul className="mt-2 space-y-0.5 text-xs text-v25-ink-700">
      {items.filter(Boolean).map((t) => <li key={t}>{t}</li>)}
    </ul>
  );
}

function Chips({ nombres, vacio }) {
  if (nombres.length === 0) return <p className="text-v25-ink-500">{vacio}</p>;
  const vistos = nombres.slice(0, MAX_NOMBRES);
  return (
    <div className="flex flex-wrap gap-1">
      {vistos.map((n, i) => (
        <span key={`${n}-${i}`} className="rounded-v25-full bg-v25-surface-sunken px-2 py-0.5 text-xs text-v25-ink-700 ring-1 ring-v25-line">{n}</span>
      ))}
      {nombres.length > vistos.length && (
        <span className="px-1 py-0.5 text-xs text-v25-ink-500">y {nombres.length - vistos.length} más</span>
      )}
    </div>
  );
}

function textoRecordatorios(p) {
  const r = [p.recordatorio_1_dias_lab_antes, p.recordatorio_2_dias_lab_antes].filter((x) => x != null);
  if (r.length === 0) return 'Sin recordatorios';
  return `Recordatorios ${r.map((d) => `${d} días lab. antes`).join(' y ')}`;
}

function fechaCorta(s) {
  if (!s) return '?';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
